import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {UserHomepageControllerContext} from "../../../../../../index";
import {createUUId} from "../../../../../../../../../core/services/utils/utils";
import {routes} from "../../../../../../../../../core/models/static/routes/app-routes";
import ToggleButtons from "../../../../../../../../components/base/button/toggle_buttons";
import Button from "../../../../../../../../components/base/button/button";
import BroadcastService from "../../../../../../../../../core/services/broadcast";
import UserHomepageControlPanelComponentSearchNexusSummary from "./summary/nexus";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {stringifyUrl} from "query-string";


const UserHomepageControlPanelComponentSearch = ({updatedComponent, tabId, updating}) => {
    const controller = useContext(UserHomepageControllerContext);
    /**@type {React.MutableRefObject<string>}*/
    const bcName = useRef();
    const [commitSearch, setCommitSearch] = useState({data: null, commit: false});
    const [rerenderer, setRerenederer] = useState(false);
    const operatorAccessLevels = useAccessLevels();

    const nexusSearchId = useMemo(() => updatedComponent?.nexusSearchId, [updatedComponent?.nexusSearchId])


    /**
     * As soon as the component un-mounts:
     * - closes the broadcast channel for this search section and all the tabs that were to bind a search query.
     */
    useEffect(() => {
        const channelName = createUUId(false)
        bcName.current = channelName;
        const {close: closeBc} = BroadcastService.addEventListener(channelName, 'message', (data) => setCommitSearch({
            data: data,
            commit: true
        }));
        return () => {
            BroadcastService.postMessage(channelName, {close: true})
            closeBc();
        }
    }, [])

    /**
     * With each change of the commit property of commitSearch state:
     * - if commit property flag is set, then sets the new search query.
     */
    useEffect(() => {
        if (!commitSearch.commit)
            return;
        setNewSearchQuery(commitSearch.data);
        setCommitSearch({commit: false, data: null})
    }, [commitSearch.commit])

    /**
     * Opens a new tab in the window with the selected search type as its search tab.
     *
     * * the point is that the opened tab can then use the injected broadcaster name to communicate back with this
     * broadcaster and sends its search results if the user wants to bind them.
     */
    const initiateSearchQueryCapture = useCallback(() => {
        const url = window.origin + (stringifyUrl({
            url: routes.lookup.search,
            query: {
                broadcaster: bcName.current,
                ...(nexusSearchId && {searchToken: nexusSearchId}),
            },
        }));
        window.open(url, '_blank');
    }, [nexusSearchId])

    /**
     * if the data event sent to this broadcaster was of a correct type:
     * * updates the controllers search query.
     * @param {any} data
     */
    const setNewSearchQuery = (data) => {
        if (data.close) {
            // event is just the one that has been posted.
            return;
        }
        // close the tabs of the browser opened by this search query.
        BroadcastService.postMessage(bcName.current, {close: true})
        setRerenederer(prevState => !prevState);
        // broadChannelRef.current?.postMessage({close: true});
        controller.updateComponent(tabId, {
            ...updatedComponent,
            ...data,
        }, false)
    }

    /**
     * Invokes the appropriate controller callback for the removal of the search query of the component.
     */
    const removeSearch = () => {
        controller.updateComponent(tabId, {
            ...updatedComponent,
            searchQuery: undefined,
            searchType: undefined,
            nexusSearchId: undefined,
        }, false)
    }

    const searchTypes = useMemo(() => {
        const result = [
            {
                name: 'Nexus Search',
                value: "Nexus Search",
                disabled: updating,
                props: {
                    onClick: () => initiateSearchQueryCapture(),
                },
            }
        ]
        if (!operatorAccessLevels.packages.lookup.nexusSearch) {
            result.splice(0, 1);
        }
        return result;
    }, [updating, initiateSearchQueryCapture, operatorAccessLevels.packages.lookup.nexusSearch])

    const summary = useMemo(() => {
        if (!nexusSearchId)
            return <></>;
        return <UserHomepageControlPanelComponentSearchNexusSummary searchToken={nexusSearchId} rerenderer={rerenderer}/>
    }, [nexusSearchId, rerenderer])

    return (
        <>
            <div className={'component-search-section'}>
                <div className={'d-flex align-items-center'}>
                    <p className={''}>
                        on More Results
                    </p>
                    <div className={'dotted-divider'}/>
                </div>
                <ToggleButtons
                    className={'toggle-buttons'}
                    value={nexusSearchId ? "Nexus Search" : null}
                    options={searchTypes}
                />
                {summary}
                {
                    !!nexusSearchId &&
                    <div className={'d-flex justify-content-center'}>
                        <Button
                            className='text-xs mt-1'
                            variant={'text'}
                            color={'error'}
                            onClick={removeSearch}
                        >
                            Remove Search Query
                        </Button>
                    </div>
                }
            </div>
        </>
    );
}
export default UserHomepageControlPanelComponentSearch;
